import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useAuth from '../../../context/auth.context.jsx';
import useGroup from '../../../context/group.context.jsx';
import expensesServices from '../../services/expensesServices';
import groupServices from '../../services/groupServices';
import Sheet from '../common/sheet.jsx';
import Button from '../common/button.jsx';
import Input from '../common/input.jsx';
import { formatMoney } from '../../utils/money.js';
import { haptic } from '../../utils/haptics.js';
import useTour from '../../../context/tour.context.jsx';

function otherPartyName(row, members) {
    const others = (row.shares || []).filter(s => s.index !== row.payerIndex);
    if (others.length !== 1) return null;
    return members[others[0].index]?.name;
}

// One `step` state inside a single Sheet — same pattern as GroupSwitcherSheet
// in components/appBar.jsx. Steps: start (explain + pick file) -> setup
// (target group + who's who) -> preview (balance check + confirm) -> done.
export default function ImportSplitwise({ open, onClose }) {
    const { t } = useTranslation(['groups', 'common']);
    const { userData, loadUserData } = useAuth();
    const { myGroups, activeGroup, activeGroupDetails, setActiveGroup, refreshGroups, refreshGroupDetails } = useGroup();
    const { holdTour } = useTour() || {};
    const me = userData?.user;
    const showTrialDisclose = userData?.entitlements?.trialMode === 'disclosed' && userData?.entitlements?.trialAvailable;

    const [step, setStep] = useState('start');
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null); // raw /import/preview response
    const [rows, setRows] = useState([]); // editable copy: preview.expenses with a mutable `type`
    const [targetMode, setTargetMode] = useState('new'); // 'new' | 'existing'
    const [newGroupName, setNewGroupName] = useState('');
    const [existingGroupId, setExistingGroupId] = useState('');
    const [mapping, setMapping] = useState([]); // aligned to preview.members: { action, userId? }
    const [result, setResult] = useState(null); // commit response
    // getMyGroups doesn't populate `members` (just raw ids), so the "map to
    // an existing member" list needs the full, populated group — reuse
    // activeGroupDetails when it's the same group, else fetch on demand.
    const [targetGroupDetails, setTargetGroupDetails] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null); // kept around so "flip direction" can re-preview
    const [flipDirection, setFlipDirection] = useState(false);
    const [flipPulse, setFlipPulse] = useState(0);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!open || !holdTour) return undefined;
        return holdTour();
    }, [open, holdTour]);

    const reset = () => {
        setStep('start'); setPreview(null); setRows([]); setTargetMode('new');
        setNewGroupName(''); setExistingGroupId(''); setMapping([]); setResult(null);
        setTargetGroupDetails(null); setSelectedFile(null); setFlipDirection(false);
        setFlipPulse(0);
    };
    const close = () => { reset(); onClose(); };

    const openFilePicker = () => fileInputRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setBusy(true);
        try {
            const data = await expensesServices.previewSplitwiseImport(file);
            setSelectedFile(file);
            setFlipDirection(false);
            setFlipPulse(0);
            setPreview(data);
            setRows(data.expenses.map(row => ({ ...row, type: row.suggestedType })));
            // Auto-guess "which one is me" by name match; everyone else
            // defaults to a placeholder the user can remap in the next step.
            setMapping(data.members.map(m => (
                me?.name && m.name?.trim().toLowerCase() === me.name.trim().toLowerCase()
                    ? { action: 'existing', userId: me._id }
                    : { action: 'placeholder' }
            )));
            const sameCurrency = myGroups.filter(g => (g.currency || 'ILS').toUpperCase() === data.currency);
            if (activeGroup && sameCurrency.some(g => g._id === activeGroup._id)) {
                setTargetMode('existing');
                setExistingGroupId(activeGroup._id);
            } else if (sameCurrency.length > 0) {
                setTargetMode('existing');
                setExistingGroupId(sameCurrency[0]._id);
            } else {
                setTargetMode('new');
            }
            setStep('setup');
        } catch (error) {
            toast.error(error.response?.data?.error || t('importGenericError'));
        } finally {
            setBusy(false);
        }
    };

    // Re-runs preview against the SAME file with the other sign mirror —
    // only offered when the file itself is ambiguous (every expense is
    // 2-person, see reconstruct.js). Members/currency/fileHash are unchanged
    // by this, so mapping and target-group choices are left alone.
    const toggleDirection = async () => {
        if (!selectedFile) return;
        setBusy(true);
        try {
            const nextFlip = !flipDirection;
            const data = await expensesServices.previewSplitwiseImport(selectedFile, nextFlip);
            setFlipDirection(nextFlip);
            setPreview(data);
            setRows(data.expenses.map(row => ({ ...row, type: row.suggestedType })));
            setFlipPulse(n => n + 1);
        } catch (error) {
            toast.error(error.response?.data?.error || t('importGenericError'));
        } finally {
            setBusy(false);
        }
    };

    const sameCurrencyGroups = preview ? myGroups.filter(g => (g.currency || 'ILS').toUpperCase() === preview.currency) : [];
    // Any group can be an import target now, not just same-currency ones —
    // a mismatch converts at today's rate (see the warning below) rather
    // than being blocked outright.
    const existingGroup = myGroups.find(g => g._id === existingGroupId);
    const currencyMismatch = !!(preview && existingGroup && (existingGroup.currency || 'ILS').toUpperCase() !== preview.currency);
    const [ratePreview, setRatePreview] = useState(null);

    useEffect(() => {
        if (!currencyMismatch) { setRatePreview(null); return; }
        let alive = true;
        groupServices.getExchangeRate(preview.currency, existingGroup.currency, 1)
            .then((res) => { if (alive) setRatePreview(res); })
            .catch(() => { if (alive) setRatePreview(null); });
        return () => { alive = false; };
    }, [currencyMismatch, preview?.currency, existingGroup?.currency]);

    // getMyGroups doesn't populate members, so fetch the full roster for
    // whichever group is selected — reusing activeGroupDetails when it's
    // the same group instead of a redundant request.
    useEffect(() => {
        if (targetMode !== 'existing' || !existingGroupId) { setTargetGroupDetails(null); return; }
        if (activeGroupDetails && activeGroupDetails._id === existingGroupId) {
            setTargetGroupDetails(activeGroupDetails);
            return;
        }
        setTargetGroupDetails(null);
        let alive = true;
        groupServices.getGroup(existingGroupId)
            .then(({ group }) => { if (alive) setTargetGroupDetails(group); })
            .catch(() => { if (alive) setTargetGroupDetails(null); });
        return () => { alive = false; };
    }, [targetMode, existingGroupId, activeGroupDetails]);

    // Real (non-placeholder) members of the chosen target group, for the
    // "map to an existing member" option — a brand-new group only ever has
    // the importer as a real member so far.
    const targetRealMembers = targetMode === 'existing'
        ? (targetGroupDetails?.members || []).filter(m => !m.isPlaceholder)
        : (me ? [{ _id: me._id, name: me.name }] : []);

    // A mapping chosen for one target can name someone who is not in the next one (the user
    // switched to a new group or another group): such an entry falls back to a temporary
    // user instead of reaching the server as an invalid mapping. Derived, not stored, so
    // switching back restores the choice.
    const realMemberIds = new Set(targetRealMembers.map(u => String(u._id)));
    const effectiveMapping = mapping.map(m => (
        m.action === 'existing' && !realMemberIds.has(String(m.userId)) ? { action: 'placeholder' } : m
    ));

    const setMemberMapping = (index, action, userId) => {
        setMapping(prev => prev.map((m, i) => (i === index ? { action, userId } : m)));
    };

    const canContinueSetup = preview
        && effectiveMapping.length === preview.members.length
        && effectiveMapping.every(m => m.action === 'placeholder' || (m.action === 'existing' && m.userId))
        && (targetMode === 'new' ? newGroupName.trim().length > 0 : !!existingGroupId);

    const goToPreview = () => setStep('preview');

    const toggleRowType = (rowNumber) => {
        setRows(prev => prev.map(r => (r.rowNumber === rowNumber
            ? { ...r, type: r.type === 'SETTLEMENT' ? 'EXPENSE' : 'SETTLEMENT' }
            : r)));
    };

    const commit = async (force = false) => {
        setBusy(true);
        try {
            const payload = {
                currency: preview.currency,
                members: preview.members,
                memberMapping: effectiveMapping,
                rows,
                declaredBalancesAgorot: preview.declaredBalancesAgorot,
                fileHash: preview.fileHash,
                force,
                ...(targetMode === 'existing' ? { groupId: existingGroupId } : { newGroup: { name: newGroupName.trim(), type: 'GENERAL' } }),
            };
            const data = await expensesServices.commitSplitwiseImport(payload);
            // The import is done once the server answers. Showing the result first, and keeping
            // the refreshes out of this try, means a failed refresh can no longer look like a
            // failed import and invite a second click that creates a second group.
            setResult(data);
            haptic(14);
            setStep('done');
            try {
                await refreshGroups();
                if (data.groupId !== activeGroup?._id) setActiveGroup(data.groupId);
                await refreshGroupDetails();
                if (data.trial?.started) await loadUserData();
            } catch { /* the data is saved; the lists catch up on the next load */ }
        } catch (error) {
            if (error.response?.data?.code === 'ALREADY_IMPORTED') {
                // await: returning the promise ran `finally` (busy off) while the forced request was still in flight.
                if (window.confirm(t('importAlreadyImportedConfirm'))) return await commit(true);
                return;
            }
            toast.error(error.response?.data?.error || t('importGenericError'));
        } finally {
            setBusy(false);
        }
    };

    const undo = async () => {
        if (!window.confirm(t('importUndoConfirm'))) return;
        setBusy(true);
        try {
            await expensesServices.undoSplitwiseImport(result.batchId);
            toast.success(t('importUndone'));
            await refreshGroups();
            await refreshGroupDetails();
            close();
        } catch (error) {
            toast.error(error.response?.data?.error || t('importGenericError'));
        } finally {
            setBusy(false);
        }
    };

    const titleByStep = {
        start: t('importTitle'),
        setup: t('importStepSetupTitle'),
        preview: t('importStepPreviewTitle'),
        done: t('importStepDoneTitle'),
    };

    return (
        <Sheet open={open} onClose={close} title={titleByStep[step]} dismissible={!busy}>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv,text/comma-separated-values,application/vnd.ms-excel" style={{ display: 'none' }} onChange={handleFile} />

            {step === 'start' && (
                <>
                    <p className="text-muted-ms">{t('importExplain1')}</p>
                    <ol className="text-muted-ms mb-3" style={{ paddingInlineStart: '1.25rem' }}>
                        <li>{t('importExplainStep1')}</li>
                        <li>{t('importExplainStep2')}</li>
                        <li>{t('importExplainStep3')}</li>
                    </ol>
                    {showTrialDisclose && (
                        <div className="ms-card mb-3" style={{ background: 'var(--color-bg)' }}>
                            <p className="fw-semibold mb-1">{t('importTrialDiscloseTitle')}</p>
                            <p className="text-muted-ms m-0" style={{ fontSize: '0.9rem' }}>{t('importTrialDiscloseBody')}</p>
                        </div>
                    )}
                    <Button
                        text={busy ? undefined : t('importChooseFile')}
                        onClick={openFilePicker}
                        disabled={busy ? 'loading' : false}
                    />
                </>
            )}

            {step === 'setup' && preview && (
                <>
                    <label className="ms-label">{t('importTargetGroup')}</label>
                    <div className="category-chips mb-3">
                        <button type="button" className={`category-chip ${targetMode === 'new' ? 'selected' : ''}`} onClick={() => setTargetMode('new')}>
                            <i className="bi bi-plus-lg" /> {t('importTargetNew')}
                        </button>
                        {myGroups.length > 0 && (
                            <button
                                type="button"
                                className={`category-chip ${targetMode === 'existing' ? 'selected' : ''}`}
                                onClick={() => {
                                    setTargetMode('existing');
                                    if (!existingGroupId) setExistingGroupId((sameCurrencyGroups[0] || myGroups[0])._id);
                                }}
                            >
                                <i className="bi bi-people" /> {t('importTargetExisting')}
                            </button>
                        )}
                    </div>

                    {targetMode === 'new' ? (
                        <Input label={t('importNewGroupName')} name="importGroupName" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} />
                    ) : (
                        <>
                            <label className="ms-label" htmlFor="import-existing-group">{t('importChooseGroup')}</label>
                            <select id="import-existing-group" className="ms-input mb-3" value={existingGroupId} onChange={e => setExistingGroupId(e.target.value)}>
                                {myGroups.map(g => (
                                    <option key={g._id} value={g._id}>{g.name} ({g.currency})</option>
                                ))}
                            </select>
                            {currencyMismatch && (
                                <div className="p-2 rounded mb-3" style={{ background: 'var(--color-warning-soft)', fontSize: '0.85rem' }}>
                                    <i className="bi bi-exclamation-triangle-fill mie-1-ms" style={{ color: 'var(--color-warning)' }} />
                                    {ratePreview
                                        ? t('importCurrencyMismatchWarning', {
                                            fileCurrency: preview.currency,
                                            groupCurrency: existingGroup.currency,
                                            rate: Number(ratePreview.rate).toFixed(4),
                                        })
                                        : t('importCurrencyMismatchWarningLoading')}
                                </div>
                            )}
                        </>
                    )}

                    <label className="ms-label mt-2">{t('importWhoIsWho')}</label>
                    <p className="text-muted-ms" style={{ fontSize: '0.85rem' }}>{t('importWhoIsWhoHint')}</p>
                    <div className="ms-card mb-3">
                        {preview.members.map((m, i) => {
                            const current = effectiveMapping[i] || { action: 'placeholder' };
                            const value = current.action === 'existing' ? `existing:${current.userId}` : 'placeholder';
                            return (
                                <div className="expense-row" key={m.index}>
                                    <div className="expense-icon"><i className="bi bi-person" /></div>
                                    <div className="flex-grow-1 fw-semibold">{m.name}</div>
                                    <select
                                        className="ms-input"
                                        style={{ maxWidth: 190 }}
                                        value={value}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === 'placeholder') setMemberMapping(i, 'placeholder');
                                            else setMemberMapping(i, 'existing', v.slice('existing:'.length));
                                        }}
                                    >
                                        <option value="placeholder">{t('importMapPlaceholder')}</option>
                                        {targetRealMembers.map(u => (
                                            <option key={u._id} value={`existing:${u._id}`}>{u.name}</option>
                                        ))}
                                    </select>
                                </div>
                            );
                        })}
                    </div>

                    <Button text={t('importContinue')} onClick={goToPreview} disabled={!canContinueSetup} />
                    <button type="button" className="btn-ms btn-ms-outline btn-ms-block mt-2" onClick={() => setStep('start')}>{t('common:back')}</button>
                </>
            )}

            {step === 'preview' && preview && (
                <>
                    {preview.balancesMatch ? (
                        <div className="p-2 rounded mb-3 text-center" style={{ background: 'var(--color-success-soft)' }}>
                            <i className="bi bi-check-circle-fill text-success-ms" /> {t('importBalanceMatch')}
                        </div>
                    ) : (
                        <div className="p-2 rounded mb-3 text-center" style={{ background: 'var(--color-danger-soft)' }}>
                            <i className="bi bi-exclamation-triangle-fill text-danger-ms" /> {t('importBalanceMismatch')}
                        </div>
                    )}

                    {preview.warnings.length > 0 && (
                        <ul className="text-muted-ms mb-3" style={{ fontSize: '0.85rem', paddingInlineStart: '1.25rem' }}>
                            {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                    )}

                    {preview.ambiguousDirection && (
                        <button
                            type="button"
                            className="btn-ms btn-ms-outline btn-ms-block mb-3"
                            onClick={toggleDirection}
                            disabled={busy}
                        >
                            <i className="bi bi-arrow-left-right mie-1-ms" /> {t('importFlipDirection')}
                        </button>
                    )}

                    <div className="ms-card mb-3" style={{ maxHeight: 320, overflowY: 'auto' }}>
                        {rows.map(row => {
                            const payer = preview.members[row.payerIndex]?.name;
                            const ower = otherPartyName(row, preview.members);
                            return (
                            <div
                                className={`expense-row${flipPulse ? ' expense-row-flash' : ''}`}
                                key={`${row.rowNumber}-${flipPulse}`}
                            >
                                <div className="flex-grow-1">
                                    <div className="fw-semibold text-truncate">{row.description || row.categoryLabel}</div>
                                    <div className="text-muted-ms" style={{ fontSize: '0.8rem' }}>
                                        {ower
                                            ? `${row.date} · ${t('importRowPaidOwes', { payer, ower })}`
                                            : `${row.date} · ${payer}`}
                                    </div>
                                </div>
                                <span className="expense-amount">{formatMoney(row.amountAgorot / 100, preview.currency)}</span>
                                {row.shares.length === 2 && (
                                    <button
                                        type="button"
                                        className="btn-ms btn-ms-outline btn-ms-sm mis-2-ms"
                                        onClick={() => toggleRowType(row.rowNumber)}
                                        title={t('importToggleTypeHint')}
                                    >
                                        {row.type === 'SETTLEMENT' ? t('importTypeSettlement') : t('importTypeExpense')}
                                    </button>
                                )}
                            </div>
                            );
                        })}
                    </div>

                    {showTrialDisclose && (
                        <div className="ms-card mb-3" style={{ background: 'var(--color-bg)' }}>
                            <p className="fw-semibold mb-1">{t('importTrialDiscloseTitle')}</p>
                            <p className="text-muted-ms m-0" style={{ fontSize: '0.9rem' }}>{t('importTrialDiscloseBody')}</p>
                        </div>
                    )}
                    <Button text={t('importConfirm')} onClick={() => commit(false)} disabled={!preview.balancesMatch || (busy ? 'loading' : false)} />
                    <button type="button" className="btn-ms btn-ms-outline btn-ms-block mt-2" onClick={() => setStep('setup')} disabled={busy}>{t('common:back')}</button>
                </>
            )}

            {step === 'done' && result && (
                <>
                    <div className="text-center py-3">
                        <i className="bi bi-check-circle-fill text-success-ms" style={{ fontSize: '2.5rem' }} />
                        <p className="fw-semibold mt-2 mb-0">{t('importDoneTitle')}</p>
                        <p className="text-muted-ms">{t('importDoneCount', { count: result.count })}</p>
                    </div>
                    {/* Shown only when SPLITWISE_TRIAL_MODE=disclosed granted it. */}
                    {result.trial?.started && (
                        <div className="ms-card text-center mb-3" style={{ background: 'var(--color-bg)' }}>
                            <p className="fw-semibold mb-1">{t('importTrialStartedTitle')}</p>
                            <p className="text-muted-ms m-0" style={{ fontSize: '0.9rem' }}>{t('importTrialStartedBody')}</p>
                        </div>
                    )}
                    <Button text={t('common:close')} onClick={close} />
                    <button type="button" className="btn-ms btn-ms-link btn-ms-block mt-2" onClick={undo} disabled={busy}>
                        {t('importUndo')}
                    </button>
                </>
            )}
        </Sheet>
    );
}
